import React from 'react';
import { Users, FileText, Save, Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export const PlansTab = ({ plans, updatePlanField, savingPlan, onSavePlan, onDeletePlan }) => (
  <>
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {plans.map((plan) => {
        const inUse = (plan.tenants_using || 0) > 0;
        return (
        <Card key={plan.key} className="bg-white border-slate-200" data-testid={`plan-card-${plan.key}`}>
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center justify-between">
              <span className="capitalize">{plan.name}</span>
              <span className="text-xs font-normal text-slate-500">{plan.tenants_using} clientes</span>
            </CardTitle>
            <CardDescription className="text-slate-500 font-mono text-xs">{plan.key}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs flex items-center gap-1"><Users className="w-3 h-3" /> Máx. utilizadores (-1 = ilimitado)</Label>
              <Input type="number" value={plan.max_users}
                onChange={(e) => updatePlanField(plan.key, 'max_users', e.target.value)}
                className="bg-white border-slate-300 text-slate-900" data-testid={`plan-users-${plan.key}`} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs flex items-center gap-1"><FileText className="w-3 h-3" /> Máx. denúncias/mês (-1 = ilimitado)</Label>
              <Input type="number" value={plan.max_reports_month}
                onChange={(e) => updatePlanField(plan.key, 'max_reports_month', e.target.value)}
                className="bg-white border-slate-300 text-slate-900" data-testid={`plan-reports-${plan.key}`} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs">Preço mensal (€)</Label>
              <Input type="number" step="0.01" value={plan.price_monthly}
                onChange={(e) => updatePlanField(plan.key, 'price_monthly', e.target.value)}
                className="bg-white border-slate-300 text-slate-900" data-testid={`plan-price-${plan.key}`} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs">Preço por denúncia extra (€)</Label>
              <Input type="number" step="0.01" value={plan.extra_report_unit_price ?? 0}
                onChange={(e) => updatePlanField(plan.key, 'extra_report_unit_price', e.target.value)}
                className="bg-white border-slate-300 text-slate-900" data-testid={`plan-unit-price-${plan.key}`} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs">Modo ao exceder limite</Label>
              <Select value={plan.default_overage_mode || 'billing_only'}
                onValueChange={(v) => updatePlanField(plan.key, 'default_overage_mode', v)}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid={`plan-overage-${plan.key}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="billing_only">Só faturação (aceita + cobra)</SelectItem>
                  <SelectItem value="require_confirmation">Exigir confirmação de add-on</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => onSavePlan(plan)} disabled={savingPlan === plan.key}
                className="flex-1 bg-indigo-700 hover:bg-amber-600 text-black" data-testid={`save-plan-${plan.key}`}>
                {savingPlan === plan.key ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="icon" disabled={savingPlan === plan.key}
                    className="border-rose-300 text-rose-700 hover:bg-rose-50 shrink-0"
                    title={inUse ? `Vai ser desativado (${plan.tenants_using} clientes usam)` : 'Eliminar permanentemente'}
                    data-testid={`delete-plan-${plan.key}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid={`delete-plan-dialog-${plan.key}`}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {inUse ? `Desativar plano "${plan.name}"?` : `Eliminar plano "${plan.name}"?`}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {inUse ? (
                        <>
                          Este plano está a ser usado por <strong>{plan.tenants_using} cliente(s)</strong>.
                          Não pode ser eliminado — será marcado como <strong>inativo</strong> e deixará de aparecer para novos clientes,
                          mas os que já o usam mantêm-no. Para eliminar permanentemente, migre esses clientes primeiro.
                        </>
                      ) : (
                        <>
                          Nenhum cliente usa este plano. Vai ser <strong>eliminado permanentemente</strong>
                          {' '}da plataforma. Esta ação não pode ser desfeita.
                        </>
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`delete-plan-cancel-${plan.key}`}>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDeletePlan(plan)}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      data-testid={`delete-plan-confirm-${plan.key}`}>
                      {inUse ? 'Desativar' : 'Eliminar'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      );})}
    </div>
    <Alert className="bg-white border-slate-200 mt-4">
      <AlertTriangle className="w-4 h-4 text-indigo-700" />
      <AlertDescription className="text-slate-600">
        Novos clientes herdam os limites do plano no momento da criação. Alterar um plano não altera clientes já existentes.
        Eliminar um plano com clientes ativos apenas o desativa — só planos sem clientes podem ser eliminados permanentemente.
      </AlertDescription>
    </Alert>
  </>
);
